import React, { useState, useEffect, useRef } from "react";
import { fabric } from "fabric";
import Swal from "sweetalert2";
import combinedServices from "../../shared/services/user-service";
import QRCode from "qrcode";
import "./DownloadNameCards.scss";
import axios from "axios";
import { jsPDF } from "jspdf";
import Header from "../clubDashboard/Header/Header";
import { useParams } from "react-router-dom";
import userConstants from "../../shared/constants/user-constants";

// Set crossOrigin attribute for Fabric.js
fabric.Object.prototype.set({
  crossOrigin: "anonymous",
});

const DownloadNameCards = () => {
  const [nameCardTemplates, setNameCardTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [previewCanvas, setPreviewCanvas] = useState(null);
  const [previewBackCanvas, setPreviewBackCanvas] = useState(null);
  const canvasRef = useRef(null);
  const backCanvasRef = useRef(null);
  const { id } = useParams();
  const loadParticipantRef = useRef();

  useEffect(() => {
    const fetchTemplates = async () => {
      const res = await combinedServices.getNameCardTemplates();
      if (res && res.data) {
        setNameCardTemplates(res.data);
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (!canvasRef.current || !backCanvasRef.current) return;

    const newCanvas = new fabric.Canvas(canvasRef.current, {
      preserveObjectStacking: true,
      selection: false,
    });

    const newBackCanvas = new fabric.Canvas(backCanvasRef.current, {
      preserveObjectStacking: true,
      selection: false,
    });

    setPreviewCanvas(newCanvas);
    setPreviewBackCanvas(newBackCanvas);

    return () => {
      newCanvas.dispose();
      newBackCanvas.dispose();
    };
  }, []);

  useEffect(() => {
    if (loadParticipantRef.current) {
      loadParticipantRef.current.click();
    }
  }, [id]);

  const fetchParticipants = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${userConstants.baseURL}event-participant?event_id=${id}&classes=true&checkins=true`
      );
      if (res && res.data) {
        setParticipants(res.data.data);
        if (res.data.data.length > 0 && selectedTemplateId) {
          await loadTemplate(selectedTemplateId);
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch participants",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadTemplate = async (templateId) => {
    if (!previewCanvas || !previewBackCanvas || participants.length === 0)
      return;

    try {
      const response = await combinedServices.getNameCardTemplateById(
        templateId
      );
      if (response && response.data) {
        const template = response.data;
        setSelectedTemplateId(templateId);

        // Clear existing canvas
        previewCanvas.clear();
        previewBackCanvas.clear();

        // Load front canvas data with CORS handling
        if (template.front_canvas) {
          const frontData = JSON.parse(template.front_canvas);
          await loadCanvasWithCORS(previewCanvas, frontData, participants[0]);
        }

        // Load back canvas data with CORS handling
        if (template.back_canvas) {
          const backData = JSON.parse(template.back_canvas);
          await loadCanvasWithCORS(
            previewBackCanvas,
            backData,
            participants[0]
          );
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error Loading Template",
        text: error.message || "Failed to load template",
      });
    }
  };

  const loadCanvasWithCORS = async (canvas, canvasData, participant) => {
    return new Promise((resolve) => {
      const modifiedData = {
        ...canvasData,
        objects: canvasData.objects.map((obj) => {
          if (obj.type === "image" && obj.src) {
            return {
              ...obj,
              crossOrigin: "anonymous",
            };
          }
          return obj;
        }),
      };

      canvas.loadFromJSON(
        modifiedData,
        async () => {
          await populateCanvasWithParticipantData(canvas, participant);
          canvas.renderAll();
          resolve();
        },
        (o, object) => {
          if (object.type === "image" && object._originalElement) {
            object._originalElement.crossOrigin = "anonymous";
          }
        }
      );
    });
  };

  const populateCanvasWithParticipantData = async (canvas, participant) => {
    return new Promise(async (resolve) => {
      if (!canvas || !participant) return resolve();

      const user = participant.user;
      console.log("🚀 ~ returnnewPromise ~ user:", participant);
      console.log("🚀 ~ returnnewPromise ~ user:", user);

      // Create mapping of template placeholders to actual data
      const dataMap = {
        "{{name}}": `${user.user_firstname} ${user.user_lastname}`,
        "{{userId}}": user.id,
        "{{email}}": user.user_email || "",
        "{{phone}}": `${user.user_areacodemobile || ""} ${
          user.user_mobile || ""
        }`.trim(),
        "{{nambaNumber}}": `${user.nambaNumber || ""}`,
      };

      // First process text objects
      canvas.forEachObject((obj) => {
        if (obj.type === "textbox") {
          let newText = obj.text;
          for (const [placeholder, value] of Object.entries(dataMap)) {
            newText = newText.replace(new RegExp(placeholder, "g"), value);
          }
          obj.set("text", newText);
        }
      });

      // Process QR code with error handling
      const qrObjects = canvas
        .getObjects()
        .filter(
          (obj) =>
            obj.type === "image" &&
            obj.src.includes(
              "https://nambaweb.s3.ap-south-1.amazonaws.com/Dynamic%20Data/1745585747052-q5fIo.png"
            )
        );

      const qrPromises = qrObjects.map(async (qrObj) => {
        try {
          const qrData = {
            userId: user.id,
            name: `${user.user_firstname} ${user.user_lastname}`,
            email: user.user_email || "",
            phone: `${user.user_areacodemobile || ""} ${
              user.user_mobile || ""
            }`.trim(),
            eventId: id,
          };

          const qrDataURL = await QRCode.toDataURL(JSON.stringify(qrData), {
            margin: 1,
            color: {
              dark: "#000000",
              light: "#ffffff00",
            },
          });

          return new Promise((qrResolve) => {
            fabric.Image.fromURL(
              qrDataURL,
              (img) => {
                if (!img) {
                  // Fallback if image creation fails
                  const errorText = new fabric.Text("QR Error", {
                    left: qrObj.left,
                    top: qrObj.top,
                    fill: "red",
                    fontSize: 12,
                  });
                  canvas.remove(qrObj);
                  canvas.add(errorText);
                  qrResolve();
                  return;
                }

                img.set({
                  left: qrObj.left,
                  top: qrObj.top,
                  scaleX: qrObj.scaleX,
                  scaleY: qrObj.scaleY,
                  angle: qrObj.angle,
                  opacity: qrObj.opacity,
                  originX: "left",
                  originY: "top",
                  crossOrigin: "anonymous",
                });

                canvas.remove(qrObj);
                canvas.add(img);
                qrResolve();
              },
              { crossOrigin: "anonymous" }
            );
          });
        } catch (error) {
          console.error("Error generating QR code:", error);
          // Fallback to error text
          const errorText = new fabric.Text("QR Error", {
            left: qrObj.left,
            top: qrObj.top,
            fill: "red",
            fontSize: 12,
          });
          canvas.remove(qrObj);
          canvas.add(errorText);
          return Promise.resolve();
        }
      });

      await Promise.all(qrPromises);
      canvas.renderAll();
      resolve();
    });
  };

const generateAllNameCardsPDF = async () => {
  if (!selectedTemplateId || participants.length === 0) {
    Swal.fire({
      icon: "warning",
      title: "Missing Information",
      text: "Please select a template and ensure participants are loaded",
    });
    return;
  }

  setIsLoading(true);
  Swal.fire({
    title: "Generating PDF",
    html: "Please wait while we generate all name cards...",
    allowOutsideClick: false,
    didOpen: () => {
      Swal.showLoading();
    },
  });

  try {
    const templateRes = await combinedServices.getNameCardTemplateById(
      selectedTemplateId
    );
    if (!templateRes || !templateRes.data)
      throw new Error("Template not found");

    const template = templateRes.data;
    const frontData = JSON.parse(template.front_canvas);
    const backData = JSON.parse(template.back_canvas);

    const tempCanvas = new fabric.StaticCanvas(null, {
      width: frontData.width || 324,
      height: frontData.height || 204,
    });

    const tempBackCanvas = new fabric.StaticCanvas(null, {
      width: backData.width || 324,
      height: backData.height || 204,
    });

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const cardWidth = 90;
    const cardHeight = 54;
    const marginX = 15;
    const marginY = 13.5;
    const pageWidth = 210;
    const pageHeight = 297;

    const cardsPerRow = 1;
    const cardsPerColumn = 5;

    const qrDataUrls = await Promise.all(
      participants.map(async (participant) => {
        const qrData = {
          userId: participant.user.id,
          name: `${participant.user.user_firstname} ${participant.user.user_lastname}`,
          email: participant.user.user_email || "",
          phone: `${participant.user.user_areacodemobile || ""} ${
            participant.user.user_mobile || ""
          }`.trim(),
          eventId: id,
        };
        return QRCode.toDataURL(JSON.stringify(qrData), {
          margin: 1,
          color: { dark: "#000000", light: "#ffffff00" },
        });
      })
    );

    for (let i = 0; i < participants.length; i++) {
      const row = i % cardsPerColumn;
      const pageIndex = Math.floor(i / cardsPerColumn);

      if (i > 0 && row === 0) {
        pdf.addPage();
      }

      pdf.setPage(pageIndex + 1);

      // === Draw full page border ===
      pdf.setDrawColor(0);
      pdf.setLineWidth(0.2);
      pdf.line(0, 0, pageWidth, 0); // top
      pdf.line(0, pageHeight, pageWidth, pageHeight); // bottom
      pdf.line(0, 0, 0, pageHeight); // left
      pdf.line(pageWidth, 0, pageWidth, pageHeight); // right

      // === Draw grid lines ===
      for (let j = 0; j <= cardsPerColumn; j++) {
        const y = marginY + j * cardHeight;
        pdf.line(0, y, pageWidth, y);
      }

      // Vertical grid lines (3 total: start, middle, end)
      const x1 = marginX;
      const x2 = marginX + cardWidth;
      const x3 = marginX + 2 * cardWidth;
      pdf.line(x1, 0, x1, pageHeight);
      pdf.line(x2, 0, x2, pageHeight);
      pdf.line(x3, 0, x3, pageHeight);

      // === Generate canvas content ===
      tempCanvas.clear();
      tempBackCanvas.clear();

      await loadCanvasWithCORS(tempCanvas, frontData, participants[i]);
      await loadCanvasWithCORS(tempBackCanvas, backData, participants[i]);

      tempCanvas.renderAll();
      tempBackCanvas.renderAll();
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const frontImage = tempCanvas.toDataURL({ format: "png", quality: 1 });
      const backImage = tempBackCanvas.toDataURL({ format: "png", quality: 1 });

      const xPos = marginX;
      const yPos = marginY + row * cardHeight;

      pdf.addImage(frontImage, "PNG", xPos, yPos, cardWidth, cardHeight);
      pdf.addImage(backImage, "PNG", xPos + cardWidth, yPos, cardWidth, cardHeight);

      // Optional: draw border around individual cards
      pdf.setDrawColor(0);
      pdf.setLineWidth(0.2);
      pdf.rect(xPos, yPos, cardWidth, cardHeight); // front
      pdf.rect(xPos + cardWidth, yPos, cardWidth, cardHeight); // back
    }

    pdf.save(`name_cards_event_${id}.pdf`);

    Swal.fire({
      icon: "success",
      title: "PDF Generated",
      text: "All name cards have been generated and downloaded as a PDF file.",
    });
  } catch (error) {
    console.error("PDF generation error:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: error.message || "Failed to generate name cards PDF",
    });
  } finally {
    setIsLoading(false);
  }
};


  return (
    <div>
      <Header />
      <div className="dbRight">
        <div className="download-name-cards-wrapper">
          <div className="download-name-cards fabric-editor">
            <h1>Print Name Cards</h1>

            <div className="controls-section">
              <div className="control-group">
                <label>Select Template:</label>
                <div className="stInner">
                  <select
                    className="formSelect"
                    value={selectedTemplateId || ""}
                    onChange={(e) => {
                      const templateId = e.target.value;
                      if (templateId) {
                        loadTemplate(templateId);
                      } else {
                        setSelectedTemplateId(null);
                        if (previewCanvas) previewCanvas.clear();
                        if (previewBackCanvas) previewBackCanvas.clear();
                      }
                    }}
                    disabled={participants.length === 0}
                  >
                    <option value="">Select Template</option>
                    {nameCardTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.template_name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={fetchParticipants}
                    disabled={!id || isLoading}
                    ref={loadParticipantRef}
                    style={{ visibility: "hidden" }}
                  >
                    {isLoading ? "Loading..." : "Load Participants"}
                  </button>
                </div>
              </div>
              <button
                onClick={generateAllNameCardsPDF}
                disabled={
                  !selectedTemplateId || participants.length === 0 || isLoading
                }
                className="download-button"
                // style={{height:"50px"}}
              >
                {isLoading ? "Generating..." : "Print"}
              </button>
            </div>

            {participants.length > 0 && (
              <div className="participants-info">
                <h5>Participants Loaded: {participants.length}</h5>
                <p>Preview (showing first participant):</p>
                <p>
                  {participants[0].user.user_firstname}{" "}
                  {participants[0].user.user_lastname}
                </p>
              </div>
            )}

            <div className="preview-container">
              <div className="canvas-preview">
                <h4>Front</h4>
                <canvas ref={canvasRef} width={324} height={204} />
              </div>
              <div className="canvas-preview">
                <h4>Back</h4>
                <canvas ref={backCanvasRef} width={324} height={204} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadNameCards;
